import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, ShieldCheck, CreditCard, Clock } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin > satisFINDERS® — Phase 1: metrics + list + status update.
 * Route: /admin/satisfinders  (platform_admin; read-only for staff with
 * the view_satisfinder_profiles permission)
 */
export default function SatisfinderAdminList() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'platform_admin';
  const [metrics, setMetrics] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [emailVerified, setEmailVerified] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        fetch(`${API}/admin/satisfinders/metrics`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        (async () => {
          const params = new URLSearchParams();
          if (q) params.set('q', q);
          if (country) params.set('country', country);
          if (emailVerified) params.set('email_verified', emailVerified);
          if (status) params.set('status', status);
          params.set('limit', '100');
          const res = await fetch(`${API}/admin/satisfinders?${params.toString()}`, { credentials: 'include' });
          return res.ok ? res.json() : { rows: [], total: 0 };
        })(),
      ]);
      setMetrics(m);
      setRows(l.rows || []);
      setTotal(l.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const suspend = async (id, next) => {
    const res = await fetch(`${API}/admin/satisfinders/${id}/status`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next }),
    });
    if (!res.ok) { toast.error('Update failed'); return; }
    toast.success(`Marked ${next}`);
    fetchAll();
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="sf-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{isAdmin ? 'satisFINDERS® Admin' : 'satisFINDERs'}</h1>
            <p className="text-sm text-[#64748B] mt-1">Overview of registered satisFINDERs, KYC + payout status, and legacy claims.</p>
            {!isAdmin && (
              <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border bg-[#EFF6FF] text-[#1A3E6F] border-[#BFDBFE]" data-testid="sf-admin-readonly-badge">
                View only
              </span>
            )}
          </div>
          {isAdmin && (
            <Link to="/admin/satisfinder-queues" className="bg-[#1A3E6F] hover:bg-[#12305A] text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="sf-admin-open-queues">
              Open review queues →
            </Link>
          )}
        </div>

        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="sf-admin-metrics">
            <MetricTile icon={Users} label="Total" value={metrics.total_satisfinders} />
            <MetricTile icon={Users} label="New today" value={metrics.new_signups_today} />
            <MetricTile icon={Users} label="New this week" value={metrics.new_signups_this_week} />
            <MetricTile icon={Mail} label="Email verified" value={metrics.email_verified} />
            <MetricTile icon={Clock} label="Pending verify" value={metrics.pending_email_verification} tone="amber" />
            <MetricTile icon={ShieldCheck} label="KYC pending" value={metrics.kyc_pending_review} tone="amber" />
            <MetricTile icon={ShieldCheck} label="KYC verified" value={metrics.kyc_verified} tone="green" />
            <MetricTile icon={CreditCard} label="Payout added" value={metrics.payout_method_added} />
            <MetricTile icon={Clock} label="Legacy pending" value={metrics.legacy_claims_pending} tone="amber" />
            <MetricTile icon={Users} label="Waitlist" value={metrics.waitlist_count} />
            <MetricTile icon={Users} label="Open countries" value={metrics.open_countries} />
          </div>
        )}

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-[#334155] mb-1">Search</label>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email"
                className="w-full px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm" data-testid="sf-admin-search" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#334155] mb-1">Country</label>
              <input value={country} onChange={(e) => setCountry(e.target.value)}
                className="px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#334155] mb-1">Email verified</label>
              <select value={emailVerified} onChange={(e) => setEmailVerified(e.target.value)}
                className="px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm">
                <option value="">All</option>
                <option value="true">Verified</option>
                <option value="false">Pending</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#334155] mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-2 border border-[#CBD5E1] rounded-lg text-sm">
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <button onClick={fetchAll} className="bg-[#1A3E6F] hover:bg-[#12305A] text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="sf-admin-apply">
              Apply
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#64748B]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Gender</th>
                  <th className="px-4 py-3">Age</th>
                  <th className="px-4 py-3">Verified</th>
                  <th className="px-4 py-3">KYC</th>
                  <th className="px-4 py-3">Payout</th>
                  <th className="px-4 py-3">Legacy</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Signup</th>
                  {isAdmin && <th className="px-4 py-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={isAdmin ? 12 : 11} className="text-center py-8 text-[#64748B]">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 12 : 11} className="text-center py-10 text-[#64748B]">No satisFINDERs found.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.satisfinder_id} className="border-b border-[#F1F5F9] last:border-b-0 hover:bg-[#F8FAFC]" data-testid={`sf-admin-row-${r.satisfinder_id}`}>
                    <td className="px-4 py-3 font-medium">
                      {/* Prefer new structured names; fall back to full_name
                          for historic records that only carry full_name. */}
                      <Link to={`/admin/satisfinders/${r.satisfinder_id}`} className="text-[#1A3E6F] hover:underline" data-testid={`sf-admin-name-link-${r.satisfinder_id}`}>
                        {(r.first_name || r.last_name)
                          ? [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(' ')
                          : (r.full_name || '—')}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{r.email}</td>
                    <td className="px-4 py-3">{r.country}</td>
                    <td className="px-4 py-3">{r.gender}</td>
                    <td className="px-4 py-3">{r.age_group}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${r.email_verified ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                        {r.email_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3">{r.kyc_status}</td>
                    <td className="px-4 py-3">{r.payout_status}</td>
                    <td className="px-4 py-3">{r.legacy_claim_status}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${r.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{(r.created_at || '').slice(0, 10)}</td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        {r.status === 'active' ? (
                          <button onClick={() => suspend(r.satisfinder_id, 'suspended')} className="text-xs text-rose-600 hover:underline">Suspend</button>
                        ) : (
                          <button onClick={() => suspend(r.satisfinder_id, 'active')} className="text-xs text-emerald-600 hover:underline">Re-activate</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 text-xs text-[#64748B] border-t border-[#F1F5F9]">
            Showing {rows.length} of {total}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function MetricTile({ icon: Icon, label, value, tone = 'slate' }) {
  const toneCls = tone === 'green' ? 'text-emerald-700 bg-emerald-50'
    : tone === 'amber' ? 'text-amber-800 bg-amber-50'
    : 'text-[#1A3E6F] bg-[#EFF6FF]';
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${toneCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] mt-2">{label}</p>
      <p className="font-['Montserrat'] text-xl font-bold mt-1">{value ?? '—'}</p>
    </div>
  );
}
